import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import {
  Package, MessageSquare, Archive, ShoppingBag, User as UserIcon,
  RefreshCw, X, Clock, CheckCircle, XCircle, MapPin, Plus, Edit2,
  Trash2, Star, ChevronRight, CreditCard, Truck, Box, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Address Form Modal ───────────────────────────────────────────────────────
const AddressModal = ({ address, onClose, onSave }) => {
  const isEdit = !!address;
  const [loading, setLoading] = useState(false);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: address?.full_name || '',
    phone: address?.phone || '',
    address_line1: address?.address_line1 || '',
    address_line2: address?.address_line2 || '',
    city: address?.city || '',
    state: address?.state || '',
    pincode: address?.pincode || '',
    country: address?.country || 'IN',
    is_default_shipping: address?.is_default_shipping || false,
    is_default_billing: address?.is_default_billing || false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePincodeBlur = async () => {
    if (form.pincode.length === 6) {
      setPincodeLoading(true);
      try {
        const res = await axios.get(`${API}/pincode/validate/${form.pincode}`, { withCredentials: true });
        if (res.data.success) {
          setForm(prev => ({ ...prev, city: res.data.city || prev.city, state: res.data.state || prev.state }));
        }
      } catch { /* ignore */ } finally {
        setPincodeLoading(false);
      }
    }
  };

  const handleSubmit = async () => {
    if (!form.full_name || !form.phone || !form.address_line1 || !form.city || !form.state || !form.pincode) {
      toast.error('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/addresses/${address.id}`, form, { withCredentials: true });
        toast.success('Address updated');
      } else {
        await axios.post(`${API}/addresses`, form, { withCredentials: true });
        toast.success('Address added');
      }
      onSave();
    } catch (err) {
      // 422 returns detail as an array of validation error objects
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg).join(', '));
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to save address');
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-red-500 transition text-sm";
  const labelClass = "block text-xs font-semibold text-gray-400 mb-1";

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-effect p-8 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">{isEdit ? 'Edit Address' : 'Add New Address'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition"><X size={24} /></button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 sm:col-span-1">
            <label className={labelClass}>Full Name *</label>
            <input name="full_name" value={form.full_name} onChange={handleChange} className={inputClass} placeholder="John Doe" />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className={labelClass}>Phone *</label>
            <input name="phone" value={form.phone} onChange={handleChange} className={inputClass} placeholder="+919876543210" />
            <p className="text-xs text-gray-500 mt-1">Include country code e.g. +91</p>
          </div>
          <div className="col-span-2">
            <label className={labelClass}>Address Line 1 *</label>
            <input name="address_line1" value={form.address_line1} onChange={handleChange} className={inputClass} placeholder="House / Flat / Street" />
          </div>
          <div className="col-span-2">
            <label className={labelClass}>Address Line 2</label>
            <input name="address_line2" value={form.address_line2} onChange={handleChange} className={inputClass} placeholder="Area / Colony (optional)" />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className={labelClass}>Pincode *</label>
            <div className="relative">
              <input
                name="pincode"
                value={form.pincode}
                onChange={handleChange}
                onBlur={handlePincodeBlur}
                className={inputClass}
                placeholder="400001"
                maxLength={6}
              />
              {pincodeLoading && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full" />
                </div>
              )}
            </div>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className={labelClass}>City *</label>
            <input name="city" value={form.city} onChange={handleChange} className={inputClass} placeholder="Mumbai" />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className={labelClass}>State *</label>
            <input name="state" value={form.state} onChange={handleChange} className={inputClass} placeholder="Maharashtra" />
          </div>
          {/* commented by danish, since only indian addresses right now 18-05-2026*/}
          {/* <div className="col-span-2 sm:col-span-1">
            <label className={labelClass}>Country</label>
            <input name="country" value={form.country} onChange={handleChange} className={inputClass} />
          </div> */}

          <div className="col-span-2 flex flex-col gap-3 mt-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" name="is_default_shipping" checked={form.is_default_shipping} onChange={handleChange} className="w-4 h-4 accent-red-500" />
              <span className="text-sm text-gray-300">Set as default shipping address</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" name="is_default_billing" checked={form.is_default_billing} onChange={handleChange} className="w-4 h-4 accent-red-500" />
              <span className="text-sm text-gray-300">Set as default billing address</span>
            </label>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-6 py-3 border border-white/20 rounded-lg hover:bg-white/5 transition font-semibold text-sm">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
          >
            {loading ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : null}
            {isEdit ? 'Update Address' : 'Save Address'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Order Detail Modal ───────────────────────────────────────────────────────
const OrderDetailModal = ({ order, onClose, onRefund, getStatusBadge, canRefund }) => {
  const [address, setAddress] = useState(null);
  const [productImages, setProductImages] = useState({});
  const [loadingAddress, setLoadingAddress] = useState(true);

  useEffect(() => {
    const fetchDetails = async () => {
      // Fetch shipping address
      try {
        const res = await axios.get(`${API}/addresses/${order.shipping_address_id}`, { withCredentials: true });
        if (res.data.success) setAddress(res.data.address);
      } catch { } finally {
        setLoadingAddress(false);
      }

      // Fetch product images via media endpoint, fallback to product image_url
      const imageMap = {};
      await Promise.all(
        order.items.map(async (item) => {
          try {
            const mediaRes = await axios.get(`${API}/products/${item.product_id}/media`);
            if (mediaRes.data.success && mediaRes.data.media.length > 0) {
              // Use first image type media
              const firstImage = mediaRes.data.media.find(m => m.file_type === 'image');
              if (firstImage) { imageMap[item.product_id] = firstImage.file_url; return; }
            }
            // Fallback to product image_url
            const productRes = await axios.get(`${API}/products/${item.product_id}`);
            if (productRes.data?.image_url) imageMap[item.product_id] = productRes.data.image_url;
          } catch { }
        })
      );
      setProductImages(imageMap);
    };

    fetchDetails();
  }, [order]);

  const paymentInfo = order.payment_response;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-effect rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 glass-effect px-8 py-5 flex justify-between items-center border-b border-white/10 rounded-t-2xl">
          <div>
            <h2 className="text-xl font-bold">Order Details</h2>
            <p className="text-xs text-gray-400 font-mono mt-0.5">{order.id}</p>
          </div>
          <div className="flex items-center gap-3">
            {getStatusBadge(order.status, order.refund_status)}
            <button onClick={onClose} className="text-gray-400 hover:text-white transition ml-2"><X size={22} /></button>
          </div>
        </div>

        <div className="px-8 py-6 space-y-6">
          {/* Order Meta */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="bg-white/5 rounded-xl p-4">
              <p className="text-xs text-gray-400 mb-1">Date</p>
              <p className="text-sm font-semibold">{new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4">
              <p className="text-xs text-gray-400 mb-1">Payment</p>
              <p className="text-sm font-semibold capitalize">{order.payment_method}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4">
              <p className="text-xs text-gray-400 mb-1">Total</p>
              <p className="text-sm font-bold accent-red">₹{order.total_amount.toLocaleString()}</p>
            </div>
          </div>

          {/* Items */}
          <div>
            <h3 className="text-sm font-bold text-gray-300 mb-3 flex items-center gap-2">
              <Box size={16} className="text-red-500" /> Items
            </h3>
            <div className="space-y-3">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-4 bg-white/5 rounded-xl p-4">
                  {productImages[item.product_id] ? (
                    <img
                      src={productImages[item.product_id]}
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Package size={24} className="text-gray-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{item.name}</p>
                    <p className="text-xs text-gray-400 mt-1">Qty: {item.quantity}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-bold text-sm">₹{(item.price * item.quantity).toLocaleString()}</p>
                    <p className="text-xs text-gray-400">₹{item.price} each</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Price Breakdown */}
          <div className="bg-white/5 rounded-xl p-4 space-y-2">
            <div className="flex justify-between text-sm text-gray-400">
              <span>Subtotal</span>
              <span>₹{order.total_amount.toLocaleString()}</span>
            </div>
            {paymentInfo?.fees && (
              <div className="flex justify-between text-sm text-gray-400">
                <span>Platform Fee</span>
                <span>₹{paymentInfo.fees}</span>
              </div>
            )}
            <div className="border-t border-white/10 pt-2 flex justify-between font-bold">
              <span>Total Paid</span>
              <span className="accent-red">₹{order.total_amount.toLocaleString()}</span>
            </div>
          </div>

          {/* Shipping Address */}
          <div>
            <h3 className="text-sm font-bold text-gray-300 mb-3 flex items-center gap-2">
              <Truck size={16} className="text-red-500" /> Shipping Address
            </h3>
            {loadingAddress ? (
              <div className="bg-white/5 rounded-xl p-4 animate-pulse h-20" />
            ) : address ? (
              <div className="bg-white/5 rounded-xl p-4 text-sm">
                <p className="font-semibold">{address.full_name}</p>
                <p className="text-gray-400 mt-1">{address.address_line1}{address.address_line2 ? `, ${address.address_line2}` : ''}</p>
                <p className="text-gray-400">{address.city}, {address.state} — {address.pincode}</p>
                <p className="text-gray-400">{address.phone}</p>
              </div>
            ) : (
              <div className="bg-white/5 rounded-xl p-4 text-sm text-gray-500">Address details not available</div>
            )}
          </div>

          {/* Payment Info */}
          {paymentInfo && (
            <div>
              <h3 className="text-sm font-bold text-gray-300 mb-3 flex items-center gap-2">
                <CreditCard size={16} className="text-red-500" /> Payment Info
              </h3>
              <div className="bg-white/5 rounded-xl p-4 text-sm space-y-2">
                {order.payment_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Payment ID</span>
                    <span className="font-mono text-xs">{order.payment_id}</span>
                  </div>
                )}
                {paymentInfo.buyer_name && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Buyer</span>
                    <span>{paymentInfo.buyer_name}</span>
                  </div>
                )}
                {paymentInfo.created_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Paid on</span>
                    <span>{new Date(paymentInfo.created_at).toLocaleString('en-IN')}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            {order.longurl && (
              <a
                href={order.longurl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg hover:bg-white/5 transition text-sm font-semibold"
              >
                <ExternalLink size={16} /> View Payment
              </a>
            )}
            {canRefund(order) && (
              <button
                onClick={() => { onClose(); onRefund(order); }}
                className="flex items-center gap-2 px-4 py-2 bg-red-600/20 text-red-500 rounded-lg hover:bg-red-600/30 transition text-sm font-semibold"
              >
                <RefreshCw size={16} /> Request Refund
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Profile ─────────────────────────────────────────────────────────────
const Profile = () => {
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [collections, setCollections] = useState([]);
  const [offers, setOffers] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [activeTab, setActiveTab] = useState('orders');
  const [loading, setLoading] = useState(true);

  // Modals
  const [refundModal, setRefundModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);

  const [orderDetailModal, setOrderDetailModal] = useState(false);
  const [detailOrder, setDetailOrder] = useState(null);

  const [addressModal, setAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [deletingAddressId, setDeletingAddressId] = useState(null);
  const [orderProductImages, setOrderProductImages] = useState({});


  const navigate = useNavigate();

  useEffect(() => { fetchUserData(); }, []);

  useEffect(() => {
    const fetchOrderImages = async () => {
      const imageMap = {};
      const productIds = [...new Set(orders.flatMap(o => o.items.map(i => i.product_id)))];
      
      await Promise.all(
        productIds.map(async (productId) => {
          try {
            const mediaRes = await axios.get(`${API}/products/${productId}/media`);
            if (mediaRes.data.success && mediaRes.data.media.length > 0) {
              const firstImage = mediaRes.data.media.find(m => m.file_type === 'image');
              if (firstImage) { imageMap[productId] = firstImage.file_url; return; }
            }
            const productRes = await axios.get(`${API}/products/${productId}`);
            if (productRes.data?.image_url) imageMap[productId] = productRes.data.image_url;
          } catch { }
        })
      );
      setOrderProductImages(imageMap);
    };

    if (orders.length > 0) fetchOrderImages();
  }, [orders]);

  const fetchUserData = async () => {
    try {
      const [userRes, ordersRes, collectionsRes, offersRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { withCredentials: true }),
        axios.get(`${API}/orders`, { withCredentials: true }),
        axios.get(`${API}/collections/my`, { withCredentials: true }),
        axios.get(`${API}/offers`, { withCredentials: true })
      ]);
      setUser(userRes.data);
      setOrders(ordersRes.data);
      setCollections(collectionsRes.data);
      setOffers(offersRes.data);
    } catch (error) {
      if (error.response?.status === 401) navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const fetchAddresses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/addresses`, { withCredentials: true });
      if (res.data.success) setAddresses(res.data.addresses);
    } catch {
      toast.error('Failed to load addresses');
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'addresses') fetchAddresses();
  }, [activeTab, fetchAddresses]);

  // ── Refund ──
  const openRefundModal = (order) => { setSelectedOrder(order); setRefundModal(true); setRefundReason(''); };
  const closeRefundModal = () => { setRefundModal(false); setSelectedOrder(null); setRefundReason(''); };

  const handleRefundRequest = async () => {
    if (!refundReason.trim()) { toast.error('Please provide a reason for refund'); return; }
    if (!selectedOrder) return;
    setRefundLoading(true);
    try {
      const response = await axios.post(`${API}/orders/refund/request`, {
        order_id: selectedOrder.id, reason: refundReason, refund_type: 'full'
      }, { withCredentials: true });
      if (response.data.success) {
        toast.success('Refund request submitted!');
        setOrders(orders.map(o => o.id === selectedOrder.id ? { ...o, refund_status: 'requested' } : o));
        closeRefundModal();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit refund request');
    } finally {
      setRefundLoading(false);
    }
  };

  // ── Address actions ──
  const handleDeleteAddress = async (id) => {
    setDeletingAddressId(id);
    try {
      await axios.delete(`${API}/addresses/${id}`, { withCredentials: true });
      toast.success('Address deleted');
      setAddresses(prev => prev.filter(a => a.id !== id));
    } catch {
      toast.error('Failed to delete address');
    } finally {
      setDeletingAddressId(null);
    }
  };

  const handleSetDefault = async (id, type) => {
    try {
      await axios.patch(`${API}/addresses/${id}/default`, { address_type: type }, { withCredentials: true });
      toast.success(`Default ${type} address updated`);
      fetchAddresses();
    } catch {
      toast.error('Failed to update default address');
    }
  };

  // ── Helpers ──
  const getStatusBadge = (status, refundStatus) => {
    if (refundStatus) {
      const refundConfig = {
        requested: { bg: 'bg-orange-600/20', text: 'text-orange-500', label: 'Refund Requested', icon: Clock },
        approved: { bg: 'bg-blue-600/20', text: 'text-blue-500', label: 'Refund Approved', icon: CheckCircle },
        completed: { bg: 'bg-green-600/20', text: 'text-green-500', label: 'Refunded', icon: CheckCircle },
        rejected: { bg: 'bg-red-600/20', text: 'text-red-500', label: 'Refund Rejected', icon: XCircle }
      };
      const config = refundConfig[refundStatus];
      if (config) {
        const Icon = config.icon;
        return <span className={`px-4 py-1 rounded-full text-sm font-semibold ${config.bg} ${config.text} flex items-center gap-2`}><Icon size={16} />{config.label}</span>;
      }
    }
    const statusConfig = {
      paid: { bg: 'bg-green-600/20', text: 'text-green-500', label: 'Completed' },
      pending: { bg: 'bg-yellow-600/20', text: 'text-yellow-500', label: 'Pending' },
      failed: { bg: 'bg-red-600/20', text: 'text-red-500', label: 'Failed' },
      refunded: { bg: 'bg-blue-600/20', text: 'text-blue-500', label: 'Refunded' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <span className={`px-4 py-1 rounded-full text-sm font-semibold ${config.bg} ${config.text}`}>{config.label}</span>;
  };

  const canRefund = (order) => order.status === 'paid' && !order.refund_status;

  const tabs = [
    { key: 'orders', label: 'Orders', icon: Package },
    { key: 'addresses', label: 'Addresses', icon: MapPin },
    // { key: 'collections', label: 'My Collections', icon: Archive }, commented by danish, will umcomment when we release it 18-05-2026
    // { key: 'offers', label: 'My Offers', icon: ShoppingBag }, commented by danish, will umcomment when we release it 18-05-2026
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center" data-testid="loading">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-black" data-testid="profile-page">
      <Navbar />

      <div className="pt-24 px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          {/* Profile Header */}
          <div className="glass-effect p-8 rounded-2xl mb-8" data-testid="profile-header">
            <div className="flex items-center space-x-6">
              {user.picture ? (
                <img src={user.picture} alt={user.name} className="w-24 h-24 rounded-full border-4 border-red-500" data-testid="user-avatar" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-red-600 flex items-center justify-center border-4 border-red-500" data-testid="user-avatar-placeholder">
                  <UserIcon size={40} />
                </div>
              )}
              <div>
                <h1 className="text-3xl font-bold mb-2" data-testid="user-name">{user.name}</h1>
                <p className="text-gray-400 mb-4" data-testid="user-email">{user.email}</p>
                <div className="flex space-x-4 text-sm">
                  <div>
                    <span className="text-2xl font-bold accent-red">{orders.length}</span>
                    <p className="text-gray-400">Orders</p>
                  </div>
                  {/* commented by danish, since only indian addresses right now 18-05-2026*/}
                  {/* <div className="border-l border-white/10 pl-4">
                    <span className="text-2xl font-bold accent-red">{collections.length}</span>
                    <p className="text-gray-400">Collections</p>
                  </div> */}
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-8 flex space-x-2 border-b border-white/10 overflow-x-auto" data-testid="profile-tabs">
            {tabs.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-6 py-3 font-semibold transition whitespace-nowrap ${
                  activeTab === key ? 'text-red-500 border-b-2 border-red-500' : 'text-gray-400 hover:text-white'
                }`}
                data-testid={`${key}-tab`}
              >
                <Icon className="inline mr-2" size={20} />
                {label}
              </button>
            ))}
          </div>

          {/* ── Orders Tab ── */}
          {activeTab === 'orders' && (
            <div className="space-y-4" data-testid="orders-content">
              {orders.length > 0 ? orders.map(order => (
                <div
                  key={order.id}
                  className="glass-effect p-6 rounded-xl cursor-pointer hover:border-red-500/30 border border-transparent transition"
                  data-testid={`order-${order.id}`}
                  onClick={() => { setDetailOrder(order); setOrderDetailModal(true); }}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <p className="text-sm text-gray-400">Order ID: {order.id.slice(0, 8)}...</p>
                      <p className="text-sm text-gray-400">{new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      {order.payment_id && <p className="text-xs text-gray-500 mt-1">Payment: {order.payment_id}</p>}
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(order.status, order.refund_status)}
                      <ChevronRight size={18} className="text-gray-500" />
                    </div>
                  </div>
                  {order.items.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm py-2">
                      {orderProductImages[item.product_id] ? (
                        <img
                          src={orderProductImages[item.product_id]}
                          alt={item.name}
                          className="w-10 h-10 object-cover rounded-lg flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-white/10 rounded-lg flex-shrink-0 flex items-center justify-center">
                          <Package size={16} className="text-gray-500" />
                        </div>
                      )}
                      <span className="flex-1">{item.name} x {item.quantity}</span>
                      <span className="font-semibold">₹{(item.price * item.quantity).toLocaleString()}</span>
                    </div>
                  ))}
                  <div className="border-t border-white/10 pt-4 flex justify-between items-center">
                    <span className="font-bold">Total</span>
                    <span className="text-2xl font-bold accent-red">₹{order.total_amount.toLocaleString()}</span>
                  </div>
                </div>
              )) : (
                <div className="text-center py-20" data-testid="no-orders">
                  <Package className="mx-auto mb-4 text-gray-600" size={60} />
                  <p className="text-gray-400">No orders yet</p>
                </div>
              )}
            </div>
          )}

          {/* ── Addresses Tab ── */}
          {activeTab === 'addresses' && (
            <div data-testid="addresses-content">
              <div className="flex justify-end mb-6">
                <button
                  onClick={() => { setEditingAddress(null); setAddressModal(true); }}
                  className="btn-primary flex items-center gap-2 px-5 py-2.5 text-sm"
                >
                  <Plus size={18} /> Add Address
                </button>
              </div>

              {addresses.length > 0 ? (
                <div className="grid sm:grid-cols-2 gap-4">
                  {addresses.map(addr => (
                    <div key={addr.id} className="glass-effect p-6 rounded-xl relative border border-transparent hover:border-white/10 transition">
                      {/* Default badges */}
                      <div className="flex gap-2 mb-3 flex-wrap">
                        {addr.is_default_shipping && (
                          <span className="px-2 py-0.5 bg-red-600/20 text-red-400 text-xs rounded-full font-semibold flex items-center gap-1">
                            <Star size={10} /> Default Shipping
                          </span>
                        )}
                        {addr.is_default_billing && (
                          <span className="px-2 py-0.5 bg-blue-600/20 text-blue-400 text-xs rounded-full font-semibold flex items-center gap-1">
                            <Star size={10} /> Default Billing
                          </span>
                        )}
                      </div>

                      <p className="font-semibold">{addr.full_name}</p>
                      <p className="text-sm text-gray-400 mt-1">{addr.address_line1}{addr.address_line2 ? `, ${addr.address_line2}` : ''}</p>
                      <p className="text-sm text-gray-400">{addr.city}, {addr.state} — {addr.pincode}</p>
                      <p className="text-sm text-gray-400 mt-1">{addr.phone}</p>

                      {/* Actions */}
                      <div className="flex items-center gap-2 mt-4 flex-wrap">
                        <button
                          onClick={() => { setEditingAddress(addr); setAddressModal(true); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg text-xs font-semibold transition"
                        >
                          <Edit2 size={13} /> Edit
                        </button>
                        <button
                          onClick={() => handleDeleteAddress(addr.id)}
                          disabled={deletingAddressId === addr.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600/10 hover:bg-red-600/20 text-red-500 rounded-lg text-xs font-semibold transition disabled:opacity-50"
                        >
                          {deletingAddressId === addr.id
                            ? <div className="animate-spin h-3 w-3 border border-red-500 border-t-transparent rounded-full" />
                            : <Trash2 size={13} />}
                          Delete
                        </button>
                        {!addr.is_default_shipping && (
                          <button
                            onClick={() => handleSetDefault(addr.id, 'shipping')}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg text-xs font-semibold transition text-gray-300"
                          >
                            <Star size={13} /> Set Shipping Default
                          </button>
                        )}
                        {!addr.is_default_billing && (
                          <button
                            onClick={() => handleSetDefault(addr.id, 'billing')}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg text-xs font-semibold transition text-gray-300"
                          >
                            <Star size={13} /> Set Billing Default
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20" data-testid="no-addresses">
                  <MapPin className="mx-auto mb-4 text-gray-600" size={60} />
                  <p className="text-gray-400 mb-4">No saved addresses yet</p>
                  <button
                    onClick={() => { setEditingAddress(null); setAddressModal(true); }}
                    className="btn-primary px-6 py-2.5 text-sm flex items-center gap-2 mx-auto"
                  >
                    <Plus size={16} /> Add Your First Address
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Collections Tab ── */}
          {activeTab === 'collections' && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="collections-content">
              {collections.length > 0 ? collections.map(collection => (
                <div key={collection.id} className="product-card" data-testid={`collection-${collection.id}`}>
                  <div className="h-48 bg-gradient-to-br from-red-900/30 to-purple-900/30 flex items-center justify-center">
                    <Archive size={64} className="text-red-500 opacity-50" />
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-semibold mb-2">{collection.name}</h3>
                    <p className="text-sm text-gray-400 line-clamp-2 mb-4">{collection.description}</p>
                    <p className="text-sm text-gray-500">{collection.items?.length || 0} items</p>
                  </div>
                </div>
              )) : (
                <div className="col-span-full text-center py-20" data-testid="no-collections">
                  <Archive className="mx-auto mb-4 text-gray-600" size={60} />
                  <p className="text-gray-400">No collections yet</p>
                </div>
              )}
            </div>
          )}

          {/* ── Offers Tab ── */}
          {activeTab === 'offers' && (
            <div className="space-y-4" data-testid="offers-content">
              {offers.length > 0 ? offers.map(offer => (
                <div key={offer.id} className="glass-effect p-6 rounded-xl" data-testid={`offer-${offer.id}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Offer for Product ID: {offer.product_id.slice(0, 8)}</p>
                      <p className="text-2xl font-bold accent-red">₹{offer.offer_amount.toLocaleString()}</p>
                      <p className="text-sm text-gray-400 mt-1">{new Date(offer.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                      offer.status === 'accepted' ? 'bg-green-600/20 text-green-500' :
                      offer.status === 'rejected' ? 'bg-red-600/20 text-red-500' :
                      'bg-yellow-600/20 text-yellow-500'
                    }`}>
                      {offer.status.charAt(0).toUpperCase() + offer.status.slice(1)}
                    </span>
                  </div>
                </div>
              )) : (
                <div className="text-center py-20" data-testid="no-offers">
                  <ShoppingBag className="mx-auto mb-4 text-gray-600" size={60} />
                  <p className="text-gray-400">No offers yet</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Order Detail Modal ── */}
      {orderDetailModal && detailOrder && (
        <OrderDetailModal
          order={detailOrder}
          onClose={() => { setOrderDetailModal(false); setDetailOrder(null); }}
          onRefund={openRefundModal}
          getStatusBadge={getStatusBadge}
          canRefund={canRefund}
        />
      )}

      {/* ── Address Form Modal ── */}
      {addressModal && (
        <AddressModal
          address={editingAddress}
          onClose={() => { setAddressModal(false); setEditingAddress(null); }}
          onSave={() => { setAddressModal(false); setEditingAddress(null); fetchAddresses(); }}
        />
      )}

      {/* ── Refund Modal ── */}
      {refundModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass-effect p-8 rounded-2xl max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Request Refund</h2>
              <button onClick={closeRefundModal} className="text-gray-400 hover:text-white transition" disabled={refundLoading}>
                <X size={24} />
              </button>
            </div>
            <div className="mb-6">
              <div className="bg-white/5 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-400 mb-2">Order ID</p>
                <p className="font-mono text-sm">{selectedOrder.id}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-400 mb-2">Order Amount</p>
                <p className="text-2xl font-bold accent-red">₹{selectedOrder.total_amount.toLocaleString()}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-sm text-gray-400 mb-2">Items</p>
                {selectedOrder.items.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm mb-1">
                    <span>{item.name} x {item.quantity}</span>
                    <span>₹{(item.price * item.quantity).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2">Reason for Refund <span className="text-red-500">*</span></label>
              <textarea
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder="Please explain why you want a refund..."
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-red-500 transition min-h-[120px] resize-none"
                disabled={refundLoading}
              />
            </div>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-400"><strong>Note:</strong> Your refund request will be reviewed by our team. Once approved, the refund will be processed within 5-7 business days.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={closeRefundModal} className="flex-1 px-6 py-3 border border-white/20 rounded-lg hover:bg-white/5 transition font-semibold" disabled={refundLoading}>
                Cancel
              </button>
              <button
                onClick={handleRefundRequest}
                disabled={refundLoading || !refundReason.trim()}
                className="flex-1 btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {refundLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div> : <RefreshCw size={18} />}
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Profile;